import React, { useState, useEffect, useRef } from 'react';
import { PixelStreamingHandler } from '../sdk/PixelStreamingHandler';
import { PixelStreamingCommunicator, MessageLog } from '../sdk/PixelStreamingCommunicator';
import { Apartment, FAKE_APARTMENT_DATA, fetchApartmentsAPI, Image } from '../data/apartmentData';
import { TopBar } from './TopBar';
import { ImageStack } from './ImageStack';
import { Lightbox } from './Lightbox';
import './UILayerComponent.css';
import * as Sentry from '@sentry/react';

// Marketing Mode Components
import { TopLeftNavigationCluster } from './marketing/TopLeftNavigationCluster';
import { CameraControls } from './marketing/CameraControls';
import { NavigationModes } from './marketing/NavigationModes';
import { CaptureOverlay } from './marketing/CaptureOverlay';
import { DisabledMarketingPopup } from './marketing/DisabledMarketingPopup';

// Read environment variable for fake data
const useFakeData = import.meta.env.VITE_USE_FAKE_APARTMENT_DATA === 'true' ||
                    import.meta.env.VITE_USE_FALLBACK === 'true';
if (useFakeData) {
  console.log('[UILayer] Using FAKE wohnung data.');
} else {
  // console.log('[UILayer] Using REAL (mocked) API call for wohnung data.'); // Keep commented out
}

// Read environment variable for marketing mode disabled state
const isMarketingModeDisabled = import.meta.env.VITE_MARKETING_MODE_DISABLED === 'true';

// Helper function to format area with German decimal comma for detail views
const formatAreaForDetail = (area: number): string => {
  return area.toFixed(1).replace('.', ',') + 'm²';
};

// Helper function to format area for list views (rounded up)
const formatAreaForList = (area: number): string => {
  return Math.ceil(area) + 'm²';
};

// Define expected placeholder views
export type PlaceholderView = 'exterior' | 'interior-main' | 'interior-stairwell' | 'interior-outside';

// --- Type for Filter Values ---
interface FilterValues {
  minRooms: string;
  minSqm: string;
  minPrice: string;
}

// Initial filter values
const initialFilters: FilterValues = { 
  minRooms: '', 
  minSqm: '', 
  minPrice: ''
};

// --- Placeholder Components (Refined) ---

// --- Main Context Block (Top Middle - Revised Interior) ---
const ContextBlock: React.FC<{ 
  mode: 'exterior' | 'interior';
  apartments: Apartment[] | null;
  isLoading: boolean;
  error: Error | null;
  selectedApartmentId: string | null;
  filters: FilterValues;
  showAllApartments: boolean;
  onFilterChange: (filterName: keyof FilterValues, value: string | boolean) => void;
  onResetFilters: () => void;
  onApartmentSelect: (apartmentId: string | null) => void;
  onMakeRequest: (apartmentId: string) => void;
  onStartTour: (apartmentId: string, tourType: 'guided' | 'manual') => void;
  onShowAllApartments: () => void;
  onBackToList: () => void;
  isInteriorExpanded: boolean;
  onToggleInteriorExpand: () => void;
  isExteriorDetailExpanded: boolean;
  onToggleExteriorDetailExpand: () => void;
  onLightboxOpen: (images: Image[], apartmentId: string) => void;
}> = ({ 
  mode, apartments, isLoading, error, selectedApartmentId, 
  filters, showAllApartments, onFilterChange, onResetFilters, onApartmentSelect,
  onMakeRequest, onStartTour, onShowAllApartments, onBackToList,
  isInteriorExpanded, onToggleInteriorExpand,
  isExteriorDetailExpanded, onToggleExteriorDetailExpand,
  onLightboxOpen
}) => {
  const isAnyFilterSet = filters.minRooms !== '' || filters.minSqm !== '' || filters.minPrice !== '' || showAllApartments;

  const filteredApartments = apartments?.filter(apt => {
    // First filter out "Vorbereitung" apartments - never show these to customers
    if (apt.status.toLowerCase() === 'vorbereitung') {
      return false;
    }
    
    if (showAllApartments) {
      // Show all apartments except Vorbereitung (verfügbar, reserviert, vermietet)
      return true;
    }
    
    // Default filtering: only show available apartments and apply user filters
    if (!apt.isAvailable) {
      return false;
    }
    
    const minRoomsNum = parseInt(String(filters.minRooms), 10);
    const minSqmNum = parseInt(String(filters.minSqm), 10);
    const maxPriceNum = parseInt(String(filters.minPrice), 10);

    const passesRooms = isNaN(minRoomsNum) || apt.totalRooms >= minRoomsNum;
    const passesSqm = isNaN(minSqmNum) || apt.areaSqFt >= minSqmNum;
    const passesPrice = isNaN(maxPriceNum) || apt.pricePerMonth <= maxPriceNum;

    return passesRooms && passesSqm && passesPrice;
  }) ?? [];

  // Find the selected wohnung data if an ID is set
  const selectedApartment = apartments?.find(apt => apt.id === selectedApartmentId);

  // Determine if we are showing the detail view within the exterior mode
  const showExteriorDetailView = mode === 'exterior' && selectedApartment != null;
  
  // Expand if filters are set OR if a wohnung detail is shown OR if showing all wohnungen
  const isExteriorListExpanded = isAnyFilterSet && mode === 'exterior';
  const isBlockExpanded = isExteriorListExpanded || showExteriorDetailView || (mode === 'interior' && isInteriorExpanded);

  const handleBackToList = () => {
    onApartmentSelect(null);
    onBackToList();
  };

  const handleShowAll = () => {
    onShowAllApartments();
  };

  // Mobile click handler to expand context block
  const handleMobileClick = () => {
    // Only handle click on mobile when not expanded
    if (window.innerWidth <= 768 && !isBlockExpanded) {
      if (mode === 'exterior') {
        handleShowAll(); // Show all wohnungen on mobile
      } else if (mode === 'interior' && selectedApartment) {
        onToggleInteriorExpand(); // Expand interior details
      }
    }
  };

  // Mobile close handler
  const handleMobileClose = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the container click
    if (mode === 'exterior') {
      handleBackToList(); // This will reset filters and close
    } else if (mode === 'interior') {
      onToggleInteriorExpand(); // Close interior details
    }
  };

  // Check if we should show mobile close button
  const showMobileCloseButton = window.innerWidth <= 768 && isBlockExpanded;

  return (
    <div 
      className={`ui-module context-block ${isBlockExpanded ? 'expanded' : 'retracted'} mode-${mode}`}
      onClick={handleMobileClick}
    >
      {/* Mobile close button */}
      {showMobileCloseButton && (
        <button className="context-block-close-btn" onClick={handleMobileClose}>
          ✕
        </button>
      )}
      
      {/* Show Detail View Header OR Filter Controls */}
      {mode === 'exterior' && !isLoading && !error && (
        showExteriorDetailView ? (
          <div className="detail-header">
            <button onClick={handleBackToList} className="back-to-list-btn">&lt; Back</button>
            <h3>{selectedApartment.name}</h3>
            <div className="detail-header-right">
              <span className={`status-indicator ${selectedApartment.status.toLowerCase()}`}>{selectedApartment.status}</span>
              <button onClick={onToggleExteriorDetailExpand} className="more-info-btn">
                {isExteriorDetailExpanded ? '▲' : '▼'}
              </button>
            </div>
          </div>
        ) : (
          <div className="filter-controls">
            {!showAllApartments ? (
              <>
                
                <label>Rooms:
                  <input
                    type="number"
                    placeholder="Any" 
                    value={filters.minRooms} 
                    onChange={(e) => onFilterChange('minRooms', e.target.value)}
                    min="0"
                  />
                </label>
                <label>m²:
                  <input
                    type="number"
                    placeholder="Any" 
                    value={filters.minSqm} 
                    onChange={(e) => onFilterChange('minSqm', e.target.value)}
                    min="0"
                  />
                </label>
                <label>Price:
                  <input
                    type="number"
                    placeholder="Any" 
                    value={filters.minPrice} 
                    onChange={(e) => onFilterChange('minPrice', e.target.value)}
                    min="0"
                  />
                </label>
                <button onClick={handleShowAll} className="show-all-btn">Show All</button>
              </>
            ) : (
              <>
                <button onClick={handleBackToList} className="back-to-list-btn">&lt; Back</button>
                <h3>All Apartments</h3>
              </>
            )}
            {(isAnyFilterSet && !showAllApartments) && <button onClick={onResetFilters}>Reset</button>}
          </div>
        )
      )}
      
      {/* Interior Mode Header */}
      {mode === 'interior' && selectedApartment && (
        <div className="interior-header">
          <h3>{selectedApartment.name}</h3>
          <div className="interior-header-right">
            <span className={`status-indicator ${selectedApartment.status.toLowerCase()}`}>{selectedApartment.status}</span>
            <button onClick={onToggleInteriorExpand} className="more-info-btn">
              {isInteriorExpanded ? '▲' : '▼'}
            </button>
          </div>
        </div>
      )}
      
      {/* Content Area - Detail, List, or Interior Placeholder */}
      <div className="context-block-content">
        {isLoading && <p>Loading apartment data...</p>}
        {error && <p className="error-message">Error: {error.message}</p>}

        {/* --- Exterior Mode Content --- */} 
        {mode === 'exterior' && !isLoading && !error && (
          <>
            {/* Wohnung Detail View */}
            {showExteriorDetailView && selectedApartment && (
              <div className="apartment-detail-view">
                {/* Retracted View: Specs Row */}
                {!isExteriorDetailExpanded && (
                  <p className="detail-specs-retracted">
                    <span>Floor: {selectedApartment.floor}</span> •
                    <span>Base Rent: {selectedApartment.coldRent}€</span> •
                    <span>Utilities: {selectedApartment.utilities}€</span> •
                    <span>Total Rent: {selectedApartment.pricePerMonth}€</span> •
                    <span>Area: {formatAreaForDetail(selectedApartment.areaSqFt)}</span> •
                    <span>Rooms: {selectedApartment.totalRooms}</span>
                  </p>
                )}

                {/* Expanded View: Two Columns */}
                {isExteriorDetailExpanded && (
                  <>
                    <div className="detail-left-col">
                      <p className="detail-specs">
                        <span>Floor: {selectedApartment.floor}</span> •
                        <span>Base Rent: {selectedApartment.coldRent}€</span> •
                        <span>Utilities: {selectedApartment.utilities}€</span> •
                        <span>Total Rent: {selectedApartment.pricePerMonth}€</span> •
                        <span>Area: {formatAreaForDetail(selectedApartment.areaSqFt)}</span> •
                        <span>Rooms: {selectedApartment.totalRooms}</span> •
                        <span className={`status-indicator ${selectedApartment.status.toLowerCase()}`}>{selectedApartment.status}</span>
                      </p>
                      <ImageStack
                        images={selectedApartment.images}
                        apartmentId={selectedApartment.id}
                        onLightboxOpen={onLightboxOpen}
                      />
                      <button className="request-button" onClick={() => onMakeRequest(selectedApartment.id)}>Send Inquiry</button>
                    </div>
                    <div className="detail-right-col">
                      <div className="tour-box">
                        <h4>Digital Tour</h4>
                        {/* <button className="tour-button" onClick={() => onStartTour(selectedApartment.id, 'guided')}>Start Guided Tour</button> */}
                        <button className="tour-button" onClick={() => onStartTour(selectedApartment.id, 'manual')}>Start Tour</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Filter Results List View */}
            {!showExteriorDetailView && isAnyFilterSet && (
              <div className="apartment-list">
                {filteredApartments.length > 0 ? (
                  <ul>
                    {filteredApartments.map(apt => (
                      <li 
                        key={apt.id} 
                        onClick={() => onApartmentSelect(apt.id)}
                        className={selectedApartmentId === apt.id ? 'selected' : ''}
                      >
                        <strong>{apt.name}</strong> - {formatAreaForList(apt.areaSqFt)}, {apt.totalRooms} Rooms, {apt.pricePerMonth}€/Month
                        <span className={`status-indicator ${apt.status.toLowerCase()}`}>{apt.status}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-results-message">No apartments available with this selection</p>
                )}
              </div>
            )}
          </>
        )}

        {/* --- Interior Mode Content --- */}
        {mode === 'interior' && selectedApartment && (
          <div className="interior-content-wrapper">
            {/* Retracted View: Specs Row */}
            {!isInteriorExpanded && (
              <p className="interior-specs-retracted">
                <span>Floor: {selectedApartment.floor}</span> •
                <span>Base Rent: {selectedApartment.coldRent}€</span> •
                <span>Utilities: {selectedApartment.utilities}€</span> •
                <span>Total Rent: {selectedApartment.pricePerMonth}€</span> •
                <span>Area: {formatAreaForDetail(selectedApartment.areaSqFt)}</span> •
                <span>Rooms: {selectedApartment.totalRooms}</span> •
                <span className={`status-indicator ${selectedApartment.status.toLowerCase()}`}>{selectedApartment.status}</span>
              </p>
            )}

            {/* Expanded View: Two Columns */}
            {isInteriorExpanded && (
              <div className="interior-detail-expanded">
                {/* Left Column */}
                <div className="detail-left-col">
                  <ImageStack 
                    images={selectedApartment.images}
                    apartmentId={selectedApartment.id}
                    className="interior-image-stack"
                    onLightboxOpen={onLightboxOpen}
                  />
                  {selectedApartment.floorplan && (
                    <div className="floorplan-preview">
                      <img src={selectedApartment.floorplan.url} alt={`${selectedApartment.floorplan.name} Grundriss`} />
                    </div>
                  )}
                  <div className="interior-actions">
                    <button className="request-button" onClick={() => onMakeRequest(selectedApartment.id)}>Send Inquiry</button>
                  </div>
                </div>
                {/* Right Column */}
                <div className="detail-right-col">
                  <h4>Apartment Details</h4>
                  <ul className="details-list">
                    <li><strong>Floor:</strong> {selectedApartment.floor}</li>
                    <li><strong>Base Rent:</strong> {selectedApartment.coldRent}€</li>
                    <li><strong>Utilities:</strong> {selectedApartment.utilities}€</li>
                    <li><strong>Total Rent:</strong> {selectedApartment.pricePerMonth}€</li>
                    <li><strong>Area:</strong> {formatAreaForDetail(selectedApartment.areaSqFt)}</li>
                    <li><strong>Rooms:</strong> {selectedApartment.totalRooms}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Message if no apartment selected in interior mode */}
        {mode === 'interior' && !selectedApartment && !isLoading && (
          <p>Apartment information not available.</p>
        )}
      </div>
    </div>
  );
};






// --- Navigation Arrows (Left/Right Middle) ---
const NavigationArrows: React.FC<{ communicator: PixelStreamingCommunicator }> = ({ communicator }) => (
  <>
    <button 
      className="ui-module nav-arrow nav-arrow-left"
      onMouseDown={() => communicator.startMoving('left')}
      onMouseUp={() => communicator.stopMoving()}
      onMouseLeave={() => communicator.stopMoving()}
    >
      {'<'}
    </button>
    <button 
      className="ui-module nav-arrow nav-arrow-right"
      onMouseDown={() => communicator.startMoving('right')}
      onMouseUp={() => communicator.stopMoving()}
      onMouseLeave={() => communicator.stopMoving()}
    >
      {'>'}
    </button>
  </>
);

// --- Context Buttons (Bottom Right Menu - Updated) ---
const ContextButtons: React.FC<{
  mode: 'exterior' | 'interior';
  communicator: PixelStreamingCommunicator;
}> = ({ mode, communicator }) => {
  const [showTourModal, setShowTourModal] = React.useState(false);

  return (
    <div className="ui-module context-buttons-menu">
      {mode === 'exterior' && (
        <>
          <button
            onClick={() => setShowTourModal(!showTourModal)}
            className="tour-button"
            title="Take a tour"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <polyline points="16 11 16 7 18 7"/>
              <line x1="16" y1="3" x2="16" y2="11"/>
            </svg>
          </button>
          {showTourModal && (
            <div className="tour-modal">
              <button onClick={() => {
                communicator.startFreeRoam('House1');
                setShowTourModal(false);
              }}>
                Explore House 1
              </button>
              <button onClick={() => {
                communicator.startFreeRoam('House2');
                setShowTourModal(false);
              }}>
                Explore House 2
              </button>
            </div>
          )}
        </>
      )}
      {mode === 'interior' && (
        <>
          <div className="button-group location-switcher">
            <button
              onClick={() => communicator.teleportTo('outside')}
              className={communicator.getCurrentTeleportLocation() === 'outside' ? 'active' : ''}
            >
              Exterior
            </button>
            <button
              onClick={() => communicator.teleportTo('stairwell')}
              className={communicator.getCurrentTeleportLocation() === 'stairwell' ? 'active' : ''}
            >
              Stairwell
            </button>
            <button
              onClick={() => communicator.teleportTo('apartment')}
              className={communicator.getCurrentTeleportLocation() === 'apartment' ? 'active' : ''}
            >
              Apartment
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// --- Furniture Toggle (Bottom Left) ---
const FurnitureToggle: React.FC<{ communicator: PixelStreamingCommunicator }> = ({ communicator }) => (
  <button
    className={`ui-module furniture-toggle-button ${communicator.isFurnitureToggled() ? 'active' : ''}`}
    onClick={() => communicator.toggleFurniture()}
  >
    Furniture On/Off
  </button>
);



// --- Tutorial Layer (Overlay) ---
interface TutorialStep {
  icon: string;
  text: string;
}

interface TutorialData {
  exterior: TutorialStep[];
  interior: TutorialStep[];
  marketing: TutorialStep[];
}

const TUTORIAL_DATA: TutorialData = {
  exterior: [
    { icon: "👋", text: "Welcome! Start your digital discovery tour!" },
    { icon: "🖱️", text: "Drag mouse/finger to look around." },
    { icon: "🏠", text: "Filter apartments, select & start tour!" }
  ],
  interior: [
    { icon: "🏠", text: "Explore your new home!" },
    { icon: "👀", text: "Drag mouse/finger to look around." },
    { icon: "👆", text: "Click to walk.." },
    { icon: "⌨️", text: "..or use arrow keys (WASD) to move." }
  ],
  marketing: [
    { icon: "📸", text: "Welcome to Marketing Mode! Create professional shots for your marketing." },
    { icon: "🎛️", text: "Use the controls in the top left to adjust views, apartments, furniture and lighting." },
    { icon: "📷", text: "Capture photos and videos with the camera tools at the bottom center. Adjust the focal length!" },
    { icon: "🎨", text: "After capture, use the Plaza AI Editor to upscale images, adjust style and fix imperfections." }
  ]
};

const TutorialLayer: React.FC<{
  mode: 'exterior' | 'interior' | 'marketing';
  isVisible: boolean;
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
}> = ({ mode, isVisible, currentStep, onNext, onPrevious, onClose }) => {
  if (!isVisible) return null;

  const tutorials = TUTORIAL_DATA[mode];
  const currentTutorial = tutorials[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorials.length - 1;

  return (
    <div className="ui-module tutorial-layer">
      <div className="tutorial-content">
        <div className="tutorial-icon">{currentTutorial.icon}</div>
        <div className="tutorial-text">{currentTutorial.text}</div>
        <div className="tutorial-controls">
          <button
            className="tutorial-btn tutorial-previous"
            onClick={onPrevious}
            disabled={isFirstStep}
          >
            ◀ Back
          </button>
          <div className="tutorial-indicators">
            {tutorials.map((_, index) => (
              <div 
                key={index} 
                className={`tutorial-indicator ${index === currentStep ? 'active' : ''}`}
              />
            ))}
          </div>
          <button
            className="tutorial-btn tutorial-next"
            onClick={isLastStep ? onClose : onNext}
          >
            {isLastStep ? 'Done' : 'Next ▶'}
          </button>
        </div>
        <button className="tutorial-close" onClick={onClose}>✕</button>
      </div>
    </div>
  );
};

// --- Debug Window Component ---
const DebugWindow: React.FC<{ 
  messages: MessageLog[];
  isVisible: boolean;
}> = ({ messages, isVisible }) => {
  return (
    <div className="debug-section">
      {isVisible && (
        <div className="debug-content">
          {messages.map((msg, index) => (
            <div 
              key={index} 
              className={`debug-message ${msg.direction}`}
            >
              <div className="debug-message-header">
                <span className="timestamp">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                <span className="direction">{msg.direction}</span>
              </div>
              <pre className="debug-message-content">
                {JSON.stringify(msg.message, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// --- Watermark Component ---
const PlazaWatermark: React.FC<{ onShowTutorial: () => void }> = ({ onShowTutorial }) => {
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Attach Sentry feedback to our custom button
    const feedback = Sentry.getFeedback();
    if (feedback && feedbackButtonRef.current) {
      feedback.attachTo(feedbackButtonRef.current, {
        formTitle: "Send Feedback",
      });
    }
  }, []);

  return (
    <>
      <div className="plaza-watermark">
        <div className="plaza-watermark__text">
          <div className="plaza-watermark__powered">Powered by Plaza</div>
          <div className="plaza-watermark__made">made by <a href="https://plaza.bizzlogic.com" target="_blank" rel="noopener noreferrer">Bizzlogic</a></div>
        </div>
        <div className="plaza-watermark__actions">
          <button
            className="plaza-watermark__help"
            onClick={onShowTutorial}
            title="Show Tutorial"
          >
            ?
          </button>
          <button
            ref={feedbackButtonRef}
            className="plaza-watermark__feedback"
            title="Send Feedback"
          >
            🐛
          </button>
        </div>
      </div>
      <div className="plaza-disclaimer">
        Non-binding Visualization
      </div>
    </>
  );
};

// --- Main UI Layer Component ---
interface UILayerProps {
  handler: PixelStreamingHandler | null;
  onPlaceholderViewChange: (view: PlaceholderView) => void;
  isStreamInteractive?: boolean;
  communicator: PixelStreamingCommunicator | null;
}

export const UILayerComponent: React.FC<UILayerProps> = ({ 
  handler, 
  onPlaceholderViewChange, 
  isStreamInteractive, 
  communicator: propCommunicator
}) => {
  const [viewMode, setViewMode] = useState<'exterior' | 'interior'>('exterior');
  const [isTutorialVisible, setIsTutorialVisible] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [hasSeenExteriorTutorial, setHasSeenExteriorTutorial] = useState(false);
  const [hasSeenInteriorTutorial, setHasSeenInteriorTutorial] = useState(false);
  const [hasSeenMarketingTutorial, setHasSeenMarketingTutorial] = useState(false);
  const [apartments, setApartments] = useState<Apartment[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [isInteriorContextExpanded, setIsInteriorContextExpanded] = useState(false);
  const [isExteriorDetailExpanded, setIsExteriorDetailExpanded] = useState(true);
  const [rotation, setRotation] = useState<number>(0);
  const [messageLog, setMessageLog] = useState<MessageLog[]>([]);
  const [isDebugVisible, setIsDebugVisible] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<Image[]>([]);
  const [lightboxApartmentId, setLightboxApartmentId] = useState<string>('');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [showAllApartments, setShowAllApartments] = useState(false);

  // Marketing Mode States
  const [isMarketingMode, setIsMarketingMode] = useState<boolean>(false);
  const [showDisabledMarketingPopup, setShowDisabledMarketingPopup] = useState<boolean>(false);
  const [captureOverlayData, setCaptureOverlayData] = useState<{
    type: 'photo' | 'video';
    url: string;
    timestamp: string;
  } | null>(null);
  const [marketingBuildingView, setMarketingBuildingView] = useState<string>('');
  const [marketingApartmentId, setMarketingApartmentId] = useState<string>('');
  const [marketingFurnitureActive, setMarketingFurnitureActive] = useState<boolean>(true);
  const [marketingLightingMood, setMarketingLightingMood] = useState<'day' | 'evening' | 'night'>('day');
  const [marketingNavigationMode, setMarketingNavigationMode] = useState<'building' | 'tour' | 'drone'>('building');
  const [marketingFocalLength, setMarketingFocalLength] = useState<number>(50);

  // Create communicator instance
  // const [communicator, setCommunicator] = useState<PixelStreamingCommunicator | null>(null); // This local state might be redundant if we always use the prop

  // Load tutorial completion status from localStorage
  useEffect(() => {
    const exteriorSeen = localStorage.getItem('plaza-tutorial-exterior-seen') === 'true';
    const interiorSeen = localStorage.getItem('plaza-tutorial-interior-seen') === 'true';
    const marketingSeen = localStorage.getItem('plaza-tutorial-marketing-seen') === 'true';
    setHasSeenExteriorTutorial(exteriorSeen);
    setHasSeenInteriorTutorial(interiorSeen);
    setHasSeenMarketingTutorial(marketingSeen);
  }, []);

  // Initialize communicator when handler is available OR if propCommunicator is passed
  useEffect(() => {
    if (propCommunicator) {
      // If communicator is passed as a prop, use it directly and set up callbacks
      // This assumes propCommunicator is the fully initialized one from BannerComponent
      // setCommunicator(propCommunicator); // Set local state if still needed, or remove local state

      // Set up rotation update callback on the propCommunicator
      propCommunicator.setRotationUpdateCallback((newRotation: number) => {
        setRotation(newRotation);
      });

      // Set up message log callback on the propCommunicator
      propCommunicator.setMessageUpdateCallback((messages: MessageLog[]) => {
        // console.log('Message log updated:', messages); // Keep commented out for potential future debugging
        setMessageLog(messages);
      });
      // No need to call setCommunicator(newCommunicator) if using propCommunicator directly

    } else if (handler && !propCommunicator) { // Fallback if propCommunicator not passed, but handler is
      console.warn('[UILayerComponent] Handler provided but propCommunicator is null. Creating local communicator. This might indicate an issue.');
      const newCommunicator = new PixelStreamingCommunicator(handler);
      // @ts-ignore
      // newCommunicator.debugId = String(Date.now() + Math.random()) + "-UILayerLocal"; // Keep commented out for potential future debugging
      // console.log(`[UILayerComponent DEBUG] Created LOCAL communicator instance. ID: ${newCommunicator.debugId}`); // Keep commented out for potential future debugging
      
      newCommunicator.setRotationUpdateCallback((newRotation: number) => {
        setRotation(newRotation);
      });
      newCommunicator.setMessageUpdateCallback((messages: MessageLog[]) => {
        // console.log('Message log updated:', messages); // Keep commented out for potential future debugging
        setMessageLog(messages);
      });
      // setCommunicator(newCommunicator); // If using local communicator state
    }
    // Ensure to use propCommunicator in dependencies if that's the primary one
  }, [handler, propCommunicator]); // Dependency on propCommunicator

  // Set up focused unit update callback when wohnungen are loaded
  useEffect(() => {
    if (propCommunicator && apartments) {
      // console.log('Setting up focused unit callback with loaded wohnungen:', apartments.map(apt => apt.id)); // Keep commented out for potential future debugging
      
      propCommunicator.setFocusedUnitUpdateCallback((unitId: string | null) => {
        // console.log('UI Layer: Received focused unit update with ID:', unitId); // Keep commented out for potential future debugging
        
        // If unitId is null, clear selection
        if (!unitId) {
          console.log('UI Layer: Clearing wohnung selection due to null unit ID');
          setSelectedApartmentId(null);
          return;
        }
        
        // Check if the wohnung exists in our loaded data
        const apartmentExists = apartments.some(apt => apt.id === unitId);
        // console.log(`UI Layer: Apartment with ID ${unitId} exists in loaded data:`, apartmentExists); // Keep commented out for potential future debugging
        
        if (!apartmentExists) {
          console.warn(`[UILayerComponent] Received unknown wohnung ID from stream: ${unitId}`);
          return; // Don't update if it doesn't exist in our data
        }
        
        // Update selected apartment ID without forced re-render logic that can cause focus issues
        setSelectedApartmentId(unitId);
      });
    }
  }, [propCommunicator, apartments]);

  // --- Data Fetching Effect ---
  useEffect(() => {
    const loadApartmentData = async () => {
      return Sentry.startSpan({
        op: 'data.load',
        name: 'Load Apartment Data'
      }, async (span: any) => {
        span.setAttribute('useFakeData', useFakeData);
        
        setIsLoading(true);
        setError(null);
        try {
          let data: Apartment[];
          if (useFakeData) {
            // Use fake data directly (simulate slight delay for consistency)
            await new Promise(resolve => setTimeout(resolve, 100)); 
            data = [...FAKE_APARTMENT_DATA];
            span.setAttribute('dataSource', 'fake');
          } else {
            // Use the (mocked) API call
            data = await fetchApartmentsAPI();
            span.setAttribute('dataSource', 'api');
          }
          span.setAttribute('apartmentCount', data.length);
          
          // Cache apartment data globally for image service
          (window as any).__PLAZA_APARTMENT_DATA__ = data;
          
          setApartments(data);
        } catch (err) {
          console.error('[UILayerComponent] Error loading wohnung data:', err);
          const error = err instanceof Error ? err : new Error('An unknown error occurred');
          
          Sentry.captureException(error, {
            extra: {
              useFakeData,
              dataSource: useFakeData ? 'fake' : 'api'
            },
            tags: {
              component: 'ui_layer_component',
              operation: 'load_apartment_data'
            }
          });
          
          setError(error);
          setApartments(null); // Clear data on error
        } finally {
          setIsLoading(false);
        }
      });
    };

    loadApartmentData();
  }, []); // Run only once on mount

  // Send initial unit values to Unreal Engine
  const sendInitialUnitValues = (apartments: Apartment[]) => {
    if (!propCommunicator) {
      console.log('[UILayer] Cannot send initial unit values: propCommunicator is null');
      return;
    }

    // Filter out Vorbereitung apartments before sending to Unreal Engine
    const vorbereitungCount = apartments.filter(apt => apt.status.toLowerCase() === 'vorbereitung').length;
    const filteredApartments = apartments.filter(apt => apt.status.toLowerCase() !== 'vorbereitung');
    
    console.log('[UILayer] Total apartments:', apartments.length, 'Vorbereitung apartments:', vorbereitungCount, 'Filtered apartments:', filteredApartments.length);
    console.log('[UILayer] Preparing to send initial unit values for wohnungen:', filteredApartments.map(apt => apt.id));

    const units = filteredApartments.map(apt => ({
      unitId: apt.id,
      roomCount: apt.totalRooms,
      squareMeters: Math.round(apt.areaSqFt),
      apartmentNumber: apt.id  // Use InterneBemerkung (e.g., W1.1) as apartment number
    }));


    const message = {
      type: "initialUnitValues",
      data: {
        units
      }
    };

    propCommunicator.sendMessage(message);
  };

  // Effect to send initial unit values when wohnungen are loaded and stream is ready
  useEffect(() => {
    if (apartments && isStreamInteractive && propCommunicator) {
      sendInitialUnitValues(apartments);
    }
  }, [apartments, isStreamInteractive, propCommunicator]);

  // --- Effect to Send Filtered IDs to PixelStreaming ---
  useEffect(() => {
    Sentry.startSpan({
      op: 'data.filter',
      name: 'Filter and Update Visible Units'
    }, (span: any) => {
      // Use propCommunicator if available, otherwise the local state (though prop should be primary)
      const currentCommunicator = propCommunicator; // Prioritize prop
      
      span.setAttribute('hasCommunicator', !!currentCommunicator);
      span.setAttribute('hasApartments', !!apartments);
      span.setAttribute('isStreamInteractive', isStreamInteractive);
      span.setAttribute('showAllApartments', showAllApartments);
      span.setAttribute('apartmentCount', apartments?.length || 0);
      
      // console.log('[UILayer DEBUG] updateVisibleUnits effect running. Communicator:', !!currentCommunicator, 'Apartments:', !!apartments, 'isStreamInteractive:', isStreamInteractive); // Keep commented out for potential future debugging
      if (currentCommunicator && apartments && isStreamInteractive) { 
        const filteredApartments = apartments.filter(apt => {
          // First filter out "Vorbereitung" apartments - never show these to customers
          if (apt.status.toLowerCase() === 'vorbereitung') {
            return false;
          }
          
          if (showAllApartments) {
            // Show all apartments except Vorbereitung (verfügbar, reserviert, vermietet)
            // When showing all, we still apply user filters
            const minRoomsNum = parseInt(String(filters.minRooms), 10);
            const minSqmNum = parseInt(String(filters.minSqm), 10);
            const maxPriceNum = parseInt(String(filters.minPrice), 10);

            const passesRooms = isNaN(minRoomsNum) || apt.totalRooms >= minRoomsNum;
            const passesSqm = isNaN(minSqmNum) || apt.areaSqFt >= minSqmNum;
            const passesPrice = isNaN(maxPriceNum) || apt.pricePerMonth <= maxPriceNum;

            return passesRooms && passesSqm && passesPrice;
          }
          
          // Default filtering: only show available apartments and apply user filters
          if (!apt.isAvailable) {
            return false;
          }
          
          // Apply user filters
          const minRoomsNum = parseInt(String(filters.minRooms), 10);
          const minSqmNum = parseInt(String(filters.minSqm), 10);
          const maxPriceNum = parseInt(String(filters.minPrice), 10);

          const passesRooms = isNaN(minRoomsNum) || apt.totalRooms >= minRoomsNum;
          const passesSqm = isNaN(minSqmNum) || apt.areaSqFt >= minSqmNum;
          const passesPrice = isNaN(maxPriceNum) || apt.pricePerMonth <= maxPriceNum;

          return passesRooms && passesSqm && passesPrice;
        });

        const visibleUnitIds = filteredApartments.map(apt => apt.id);
        
        span.setAttribute('filteredCount', filteredApartments.length);
        span.setAttribute('visibleUnitIds', visibleUnitIds.join(','));
        
        // Always set isFiltered to true to ensure Unreal Engine respects our filtered list
        // This works around an issue where Unreal shows all units when isFiltered is false
        const isFiltered = true;
        
        currentCommunicator.updateVisibleUnits(visibleUnitIds, isFiltered);
        // console.log('[UILayer] Sent updated visible units to PixelStreaming:', visibleUnitIds, 'isFiltered:', isFiltered); // Keep commented out for potential future debugging
      }
    });
    // Add propCommunicator to dependencies
  }, [filters, apartments, propCommunicator, isStreamInteractive, showAllApartments]);

  // Monitor selected apartment ID changes
  useEffect(() => {
    // console.log('[UILayer] Selected apartment ID changed to:', selectedApartmentId); // Keep commented out for potential future debugging
  }, [selectedApartmentId]);

  // --- Filter Handlers ---
  const handleFilterChange = (filterName: keyof FilterValues, value: string | boolean) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setSelectedApartmentId(null);
    setShowAllApartments(false);
    if (propCommunicator) {
      propCommunicator.resetFocus();
    }
  };

  const handleBackToList = () => {
    setSelectedApartmentId(null);
    setShowAllApartments(false);
    if (propCommunicator) {
      propCommunicator.resetFocus();
    }
  };

  // --- Wohnung Selection Handler ---
  const handleApartmentSelect = (apartmentId: string | null) => {
    const currentCommunicator = propCommunicator;
    // console.log('Selected Apartment ID:', apartmentId); // Keep commented out for potential future debugging
    
    // If selecting the same wohnung that's already selected, deselect it
    if (selectedApartmentId === apartmentId && apartmentId !== null) {
      setSelectedApartmentId(null);
      if (currentCommunicator) {
        currentCommunicator.resetFocus();
      }
      return;
    }
    
    setSelectedApartmentId(apartmentId);
    if (apartmentId && currentCommunicator) {
      currentCommunicator.focusUnit(apartmentId);
    } else if (apartmentId === null && currentCommunicator) {
      currentCommunicator.resetFocus();
    }
  };

  // --- Placeholder Handlers for Detail View Actions ---
  const handleMakeRequest = (apartmentId: string) => {
    const contactElement = document.getElementById('kontakt');
    const messageField = document.getElementById('form-field-message') as HTMLTextAreaElement;
    
    if (contactElement) {
      // Find the selected wohnung
      const selectedApartment = apartments?.find(apt => apt.id === apartmentId);
      
      if (selectedApartment && messageField) {
        // Create a message with apartment details
        const message = `I am interested in the apartment:\n${selectedApartment.name}\n\n`;
        messageField.value = message;
      }
      
      contactElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // --- Tour Start Handler (handleStartTour) ---
  const handleStartTour = (apartmentId: string, tourType: 'guided' | 'manual') => {
    // console.log(`Start ${tourType} tour clicked for apartment:`, apartmentId); // Keep commented out for potential future debugging
    setSelectedApartmentId(apartmentId);
    setViewMode('interior');
    onPlaceholderViewChange('interior-main');
    if (propCommunicator) {
      propCommunicator.startTour(apartmentId, tourType);
    }
  };

  // --- Interior Context Expansion Handler ---
  const handleToggleInteriorExpand = () => {
    setIsInteriorContextExpanded(!isInteriorContextExpanded);
  };

  // --- Exterior Detail Expansion Handler ---
  const handleToggleExteriorDetailExpand = () => {
    setIsExteriorDetailExpanded(!isExteriorDetailExpanded);
  };

  // --- Effect to update parent when viewMode changes
  // This handles the basic exterior/interior switch
  useEffect(() => {
    if (viewMode === 'exterior') {
      onPlaceholderViewChange('exterior');
    } else {
      // When switching TO interior, default to main view 
      // (handleStartTour already handles this, but good fallback)
      // Later, we'll need state for the *specific* interior location
      onPlaceholderViewChange('interior-main'); 
    }
  }, [viewMode, onPlaceholderViewChange]);

  // Auto-trigger tutorials on first visit to each environment
  useEffect(() => {
    // Only trigger if stream is interactive and we're not already showing a tutorial
    if (!isStreamInteractive || isTutorialVisible) return;

    if (viewMode === 'exterior' && !hasSeenExteriorTutorial) {
      // Small delay to let the environment load
      const timer = setTimeout(() => {
        handleShowTutorial();
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (viewMode === 'interior' && !hasSeenInteriorTutorial) {
      // Small delay to let the environment load
      const timer = setTimeout(() => {
        handleShowTutorial();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [viewMode, hasSeenExteriorTutorial, hasSeenInteriorTutorial, isStreamInteractive, isTutorialVisible]);

  // --- Back To Exterior Handler (from BackToExteriorButton) ---
  const handleBackToExterior = () => {
    if (propCommunicator) {
      propCommunicator.goBackToExterior();
      // Wait a bit before re-focusing to allow the exterior view to load
      if (selectedApartmentId) {
        // console.log('Back to Exterior: Maintaining focus on unit:', selectedApartmentId); // Keep commented out for potential future debugging
        setTimeout(() => {
          if (propCommunicator) {
            propCommunicator.focusUnit(selectedApartmentId);
          }
        }, 500);
      }
    }
    setViewMode('exterior');
    setIsInteriorContextExpanded(false);
  };
  
  // Cleanup communicator on unmount
  useEffect(() => {
    return () => {
      propCommunicator?.cleanup();
    };
  }, [propCommunicator]);

  // Prevent arrow key scrolling when pixel streaming is active
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the pressed key is an arrow key
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
        // Prevent the default scrolling behavior
        event.preventDefault();
      }
    };

    // Add event listener to capture arrow keys
    document.addEventListener('keydown', handleKeyDown, { passive: false });

    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty dependency array - set up once on mount

  // --- Tutorial Handlers ---
  const handleTutorialNext = () => {
    const currentMode = isMarketingMode ? 'marketing' : viewMode;
    const maxSteps = TUTORIAL_DATA[currentMode].length;
    if (tutorialStep < maxSteps - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      // Last step reached, close tutorial
      handleTutorialClose();
    }
  };

  const handleTutorialPrevious = () => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const handleTutorialClose = () => {
    setIsTutorialVisible(false);
    setTutorialStep(0);

    // Mark tutorial as seen for the current mode
    if (viewMode === 'exterior' && !isMarketingMode) {
      localStorage.setItem('plaza-tutorial-exterior-seen', 'true');
      setHasSeenExteriorTutorial(true);
    } else if (viewMode === 'interior' && !isMarketingMode) {
      localStorage.setItem('plaza-tutorial-interior-seen', 'true');
      setHasSeenInteriorTutorial(true);
    } else if (isMarketingMode) {
      localStorage.setItem('plaza-tutorial-marketing-seen', 'true');
      setHasSeenMarketingTutorial(true);
    }
  };

  const handleShowTutorial = () => {
    setTutorialStep(0); // Reset to first step
    setIsTutorialVisible(true);
  };

  const handleResetTutorials = () => {
    localStorage.removeItem('plaza-tutorial-exterior-seen');
    localStorage.removeItem('plaza-tutorial-interior-seen');
    localStorage.removeItem('plaza-tutorial-marketing-seen');
    setHasSeenExteriorTutorial(false);
    setHasSeenInteriorTutorial(false);
    setHasSeenMarketingTutorial(false);
    console.log('Tutorial completion status reset');
  };

  // --- Lightbox Handlers ---
  const handleLightboxOpen = (images: Image[], apartmentId: string) => {
    setLightboxImages(images);
    setLightboxApartmentId(apartmentId);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setLightboxImages([]);
    setLightboxApartmentId('');
  };

  // --- Marketing Mode Handlers ---
  const handleToggleMarketingMode = () => {
    const newMarketingMode = !isMarketingMode;
    setIsMarketingMode(newMarketingMode);

    // If marketing mode is disabled, show the popup instead of the tutorial
    if (newMarketingMode && isMarketingModeDisabled) {
      setTimeout(() => {
        setShowDisabledMarketingPopup(true);
      }, 500);
      return;
    }

    // Trigger tutorial when entering marketing mode for the first time (only if not disabled)
    if (newMarketingMode && !hasSeenMarketingTutorial && !isTutorialVisible && !isMarketingModeDisabled) {
      setTimeout(() => {
        handleShowTutorial();
      }, 500);
    }
  };

  const handleCloseDisabledMarketingPopup = () => {
    setShowDisabledMarketingPopup(false);
  };

  const handleRequestDemo = () => {
    // Scroll to contact form
    const contactElement = document.getElementById('kontakt');
    const messageField = document.getElementById('form-field-message') as HTMLTextAreaElement;

    if (contactElement) {
      if (messageField) {
        const message = `I am interested in a demo of Marketing Mode.\n\n`;
        messageField.value = message;
      }

      contactElement.scrollIntoView({ behavior: 'smooth' });
    }

    // Close the popup
    setShowDisabledMarketingPopup(false);
  };

  const handleCapturePhoto = () => {
    if (!handler) return;

    const videoElement = handler.getVideoElement();
    if (!videoElement) {
      console.warn('Video element not found');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.warn('Could not get canvas context');
      return;
    }

    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        console.warn('Could not create blob from canvas');
        return;
      }

      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      setCaptureOverlayData({
        type: 'photo',
        url,
        timestamp: `Bildaufnahme ${timestamp}`
      });
    }, 'image/png');
  };

  const handleCloseCaptureOverlay = () => {
    if (captureOverlayData?.url) {
      URL.revokeObjectURL(captureOverlayData.url);
    }
    setCaptureOverlayData(null);
  };

  if (!propCommunicator) {
    console.warn('[UILayerComponent] Rendering null because propCommunicator is not available.');
    return null;
  }

  return (
    <div className={`plaza-banner__ui-layer ${isTutorialVisible ? 'tutorial-active' : ''}`}>
      
      {/* New TopBar System with ContextBlock as children */}
      <TopBar
        viewMode={viewMode}
        rotation={rotation}
        communicator={propCommunicator}
        handler={handler}
        onBackToExterior={handleBackToExterior}
        isMarketingMode={isMarketingMode}
        onToggleMarketingMode={handleToggleMarketingMode}
      >
        {/* ContextBlock is now rendered inside TopBar center section */}
        <ContextBlock 
          mode={viewMode} 
          apartments={apartments}
          isLoading={isLoading}
          error={error}
          selectedApartmentId={selectedApartmentId}
          filters={filters}
          showAllApartments={showAllApartments}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          onApartmentSelect={handleApartmentSelect}
          onMakeRequest={handleMakeRequest}
          onStartTour={handleStartTour}
          onShowAllApartments={() => setShowAllApartments(true)}
          onBackToList={handleBackToList}
          isInteriorExpanded={isInteriorContextExpanded}
          onToggleInteriorExpand={handleToggleInteriorExpand}
          isExteriorDetailExpanded={isExteriorDetailExpanded}
          onToggleExteriorDetailExpand={handleToggleExteriorDetailExpand}
          onLightboxOpen={handleLightboxOpen}
        />
      </TopBar>

      {/* Tutorial Overlay - Show for both standard and marketing modes */}
      <TutorialLayer
        mode={isMarketingMode ? 'marketing' : viewMode}
        isVisible={isTutorialVisible}
        currentStep={tutorialStep}
        onNext={handleTutorialNext}
        onPrevious={handleTutorialPrevious}
        onClose={handleTutorialClose}
      />

      {/* Standard UI - Hidden when marketing mode is active */}
      {!isMarketingMode && (
        <>
          {/* Context Buttons Menu - Pass prop */}
          <ContextButtons mode={viewMode} communicator={propCommunicator} />

          {/* Navigation Arrows - Only for exterior */}
          {viewMode === 'exterior' && (
            <NavigationArrows communicator={propCommunicator} />
          )}

          {/* Interior Only Modules */}
          {viewMode === 'interior' && (
            <FurnitureToggle communicator={propCommunicator} />
          )}
        </>
      )}

      {/* Dev Controls with Debug Window */}
      {process.env.NODE_ENV === 'development' && (
        <div className="dev-controls">
          <div className="dev-controls-main">
          <strong>Dev Controls:</strong>
          <button onClick={() => setViewMode('exterior')} disabled={viewMode === 'exterior'}>Set Exterior</button>
          <button onClick={() => setViewMode('interior')} disabled={viewMode === 'interior'}>Set Interior</button>
          <button onClick={handleShowTutorial}>Show Tutorial</button>
          <button onClick={handleResetTutorials}>Reset Tutorials</button>
            <button onClick={() => setIsDebugVisible(!isDebugVisible)}>{isDebugVisible ? 'Hide Debug' : 'Show Debug'}</button>
          <span>Mode: {viewMode}</span>
          </div>
          <DebugWindow messages={messageLog} isVisible={isDebugVisible} />
        </div>
      )}

      {/* Watermark Component */}
      <PlazaWatermark onShowTutorial={handleShowTutorial} />

      {/* Marketing Mode UI - Conditionally rendered */}
      {isMarketingMode && (
        <>
          {/* Top Left Navigation Cluster */}
          <TopLeftNavigationCluster
            communicator={propCommunicator}
            buildingView={marketingBuildingView}
            apartmentId={marketingApartmentId}
            furnitureActive={marketingFurnitureActive}
            lightingMood={marketingLightingMood}
            onBuildingViewChange={setMarketingBuildingView}
            onApartmentIdChange={setMarketingApartmentId}
            onFurnitureToggle={() => setMarketingFurnitureActive(prev => !prev)}
            onLightingMoodChange={setMarketingLightingMood}
            disabled={isMarketingModeDisabled}
          />

          {/* Bottom Center Camera Controls */}
          <CameraControls
            focalLength={marketingFocalLength}
            onFocalLengthChange={setMarketingFocalLength}
            onCapturePhoto={handleCapturePhoto}
            onRecordVideo={() => console.log('Video recording not yet implemented')}
            isRecording={false}
            disabled={isMarketingModeDisabled}
          />

          {/* Bottom Right Navigation Modes */}
          <NavigationModes
            activeMode={marketingNavigationMode}
            onModeChange={setMarketingNavigationMode}
            communicator={propCommunicator}
            disabled={isMarketingModeDisabled}
          />
        </>
      )}

      {/* Capture Overlay - Appears after capture */}
      {captureOverlayData && (
        <CaptureOverlay
          captureData={captureOverlayData}
          onClose={handleCloseCaptureOverlay}
          onSave={handleCloseCaptureOverlay}
          onDelete={handleCloseCaptureOverlay}
        />
      )}

      {/* Lightbox - Rendered at top level for full overlay */}
      {isLightboxOpen && (
        <Lightbox
          images={lightboxImages}
          initialIndex={0}
          onClose={handleLightboxClose}
          apartmentId={lightboxApartmentId}
        />
      )}

      {/* Disabled Marketing Mode Popup */}
      {showDisabledMarketingPopup && (
        <DisabledMarketingPopup
          onClose={handleCloseDisabledMarketingPopup}
          onRequestDemo={handleRequestDemo}
        />
      )}
    </div>
  );
}; 